import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MonitorSmartphone,
  Smartphone,
  Tablet,
  Laptop,
  HardDrive,
  Watch,
  MoreVertical,
  ExternalLink,
  RefreshCw,
  Lock,
  Unlock,
  Power,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { normalizeStatus } from "@/hooks/useDevices";
import type { Device } from "@/types/device";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import * as devicesApi from "@/services/devicesApi";
import { useLongPress } from "@/hooks/useLongPress";
import { MobileRowActionsSheet } from "@/components/ui/mobile-row-actions-sheet";
import type { ActionGroup } from "@/components/ui/actions-dropdown";

interface MobileDeviceListProps {
  devices: Device[];
  searchQuery?: string;
}

type ConfirmType = "lock" | "unlock" | "unenroll" | "checkin";

function iconForDevice(device: Device) {
  const os = (device.os_type || "").toLowerCase();
  const model = String((device as { model?: string }).model || "").toLowerCase();
  if (os.includes("ios") || /iphone/.test(model)) return Smartphone;
  if (/ipad/.test(model) || os.includes("ipados")) return Tablet;
  if (os.includes("mac") || /macbook|imac|mac/.test(model)) return Laptop;
  if (/watch/.test(model) || os.includes("watchos")) return Watch;
  if (/tv|appletv/.test(model) || os.includes("tvos")) return HardDrive;
  return MonitorSmartphone;
}

function relativeTime(iso?: string | null) {
  if (!iso) return "Never";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "Unknown";
  const diff = Date.now() - t;
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "Just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  if (d < 30) return `${Math.floor(d / 7)}w ago`;
  return `${Math.floor(d / 30)}mo ago`;
}

const STATUS_STYLES: Record<string, { dot: string; label: string }> = {
  active: { dot: "bg-success", label: "Active" },
  inactive: { dot: "bg-muted-foreground/40", label: "Inactive" },
  pending: { dot: "bg-warning", label: "Pending" },
  unknown: { dot: "bg-muted-foreground/40", label: "Unknown" },
};

const CONFIRM_COPY: Record<ConfirmType, { title: string; desc: (n: string) => string; label: string; destructive?: boolean }> = {
  checkin: { title: "Check In Device", desc: (n) => `Trigger a check-in for "${n}"?`, label: "Check In" },
  lock: { title: "Lock Device", desc: (n) => `Are you sure you want to lock "${n}"? The device will be remotely locked.`, label: "Lock" },
  unlock: { title: "Unlock Device", desc: (n) => `Are you sure you want to unlock "${n}"?`, label: "Unlock" },
  unenroll: { title: "Unenroll Device", desc: (n) => `Are you sure you want to unenroll "${n}"? This action cannot be undone.`, label: "Unenroll", destructive: true },
};

interface RowProps {
  device: Device;
  onOpenActions: (device: Device) => void;
}

function DeviceRow({ device, onOpenActions }: RowProps) {
  const navigate = useNavigate();
  const Icon = iconForDevice(device);
  const status = normalizeStatus(device.status);
  const meta = STATUS_STYLES[status] ?? STATUS_STYLES.unknown;
  const owner =
    (device as { fullName?: string }).fullName ||
    device.username ||
    device.userEmail ||
    "Unassigned";
  const osLabel = [device.os_type, device.os_version].filter(Boolean).join(" ").trim();
  const name = device.name || device.hostname || device.serial_number || "Unnamed device";

  const { handlers, didFireRef } = useLongPress(() => onOpenActions(device));

  return (
    <li>
      <div
        className={cn(
          "group flex w-full items-center gap-3 rounded-xl border border-border bg-card p-3 text-left",
          "transition-colors hover:bg-accent/40 active:bg-accent/60",
          "min-h-[64px]"
        )}
      >
        <button
          type="button"
          {...handlers}
          onClick={() => {
            if (didFireRef.current) {
              didFireRef.current = false;
              return;
            }
            navigate(`/devices/${device.id}`);
          }}
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
          aria-label={`Open ${name}. Long-press for actions.`}
        >
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-muted">
            <Icon className="h-5 w-5 text-muted-foreground" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="truncate text-sm font-medium text-foreground">{name}</p>
              <span
                className={cn("h-1.5 w-1.5 shrink-0 rounded-full", meta.dot)}
                aria-label={meta.label}
                title={meta.label}
              />
            </div>
            <p className="mt-0.5 truncate text-xs text-muted-foreground">
              {osLabel || "—"} · {owner}
            </p>
            <p className="mt-0.5 truncate text-[11px] text-muted-foreground/80">
              Last seen {relativeTime(device.last_seen || device.created_at)}
            </p>
          </div>
        </button>

        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0"
          aria-label={`Actions for ${name}`}
          onClick={(e) => {
            e.stopPropagation();
            onOpenActions(device);
          }}
        >
          <MoreVertical className="h-4 w-4" />
        </Button>
      </div>
    </li>
  );
}

export function MobileDeviceList({ devices, searchQuery }: MobileDeviceListProps) {
  const navigate = useNavigate();
  const [sheetDevice, setSheetDevice] = useState<Device | null>(null);
  const [confirm, setConfirm] = useState<{ type: ConfirmType; device: Device } | null>(null);
  const [loading, setLoading] = useState(false);

  const runConfirm = async () => {
    if (!confirm) return;
    setLoading(true);
    try {
      const { type, device } = confirm;
      switch (type) {
        case "lock":
          await devicesApi.bulkLock({ deviceIds: [device.id] });
          toast.success(`Lock command sent to ${device.name}`);
          break;
        case "unlock":
          await devicesApi.bulkUnlock({ deviceIds: [device.id] });
          toast.success(`Unlock command sent to ${device.name}`);
          break;
        case "unenroll":
          await devicesApi.bulkUnenroll({ deviceIds: [device.id] });
          toast.success(`${device.name} has been unenrolled`);
          break;
        case "checkin":
          await devicesApi.checkInDevice({ uuid: device.id });
          toast.success(`Check-in triggered for ${device.name}`);
          break;
      }
    } catch (err) {
      console.error(err);
      toast.error(`Failed to ${confirm.type} device`);
    } finally {
      setLoading(false);
      setConfirm(null);
    }
  };

  if (devices.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <MonitorSmartphone className="mb-3 h-10 w-10 text-muted-foreground/60" />
        <p className="text-sm font-medium text-foreground">No devices match</p>
        <p className="mt-1 text-xs text-muted-foreground">
          {searchQuery ? "Try a different search term." : "Enroll a device to get started."}
        </p>
      </div>
    );
  }

  const actionsFor = (d: Device) => [
    { label: "View Device Details", icon: ExternalLink, onClick: () => navigate(`/devices/${d.id}`) },
    {
      label: "Management",
      items: [{ label: "Check In", icon: RefreshCw, onClick: () => setConfirm({ type: "checkin", device: d }) }],
    } as ActionGroup,
    {
      label: "System",
      items: [
        { label: "Lock Device", icon: Lock, onClick: () => setConfirm({ type: "lock", device: d }) },
        { label: "Unlock Device", icon: Unlock, onClick: () => setConfirm({ type: "unlock", device: d }) },
        { label: "Unenroll Device", icon: Power, onClick: () => setConfirm({ type: "unenroll", device: d }), destructive: true },
      ],
    } as ActionGroup,
  ];

  const confirmCopy = confirm ? CONFIRM_COPY[confirm.type] : null;

  return (
    <>
      <ul className="space-y-2">
        {devices.map((d) => (
          <DeviceRow key={d.id} device={d} onOpenActions={setSheetDevice} />
        ))}
      </ul>

      {sheetDevice && (
        <MobileRowActionsSheet
          open={!!sheetDevice}
          onOpenChange={(open) => !open && setSheetDevice(null)}
          title={sheetDevice.name || sheetDevice.hostname || "Device"}
          subtitle={[sheetDevice.os_type, sheetDevice.os_version].filter(Boolean).join(" ") || sheetDevice.serial_number}
          actions={actionsFor(sheetDevice)}
        />
      )}

      <AlertDialog open={!!confirm} onOpenChange={(open) => !open && !loading && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmCopy?.title}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm && confirmCopy?.desc(confirm.device.name || "this device")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={runConfirm}
              disabled={loading}
              className={confirmCopy?.destructive ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""}
            >
              {loading ? "Working…" : confirmCopy?.label}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default MobileDeviceList;
